import React, { useState, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import CompetitionForm from "./CompetitionForm";
import ICTSocietyPage from "./ICTSocietyPage";
import CompetitionSubmitForm from "./CompetitionSubmitForm";

import {
  AlgorithmChallengeDetails,
  ProblemSolvingDetails,
  WebDevChallengeDetails,
  PhotoManipulationDetails,
  PosterDesignDetails,
  LogoDesignDetails,
  UIUXDesignDetails,
  LineBotDetails,
  InnovationChallengeDetails,
  QuizChallengeDetails,
} from "./CompetitionDetails";

import "./Competitions.css";


// Map competition code titles to images
const competitionImages = {
  "Algorithm": "ALGo.png",
  "Real-world Problem Solving": "RWPS.png",
  "Web Development": "WD.png",
  "Photo Manipulation": "pM.png",
  "Poster Design": "PD.png",
  "Logo Design": "LD.png",
  "UI/UX Design": "UIUX.png",
  "Fastest Line Following Bot": "FLFB.png",
  "Innovation Challenge": "IC.png",
  "Inter School Quiz": "QUIZ.png",
};

const competitionsData = [
  {
    school: "Armitage",
    categories: [
      { name: "Algorithm", items: [{ title: "Algorithm", detailsComponent: AlgorithmChallengeDetails }] },
      { name: "Real-world Problem Solving", items: [{ title: "Real-world Problem Solving", detailsComponent: ProblemSolvingDetails }] },
      { name: "Web Development", items: [{ title: "Web Development", detailsComponent: WebDevChallengeDetails }] },
    ],
  },
  {
    school: "Insignia",
    categories: [
      { name: "Photo Manipulation", items: [{ title: "Photo Manipulation", detailsComponent: PhotoManipulationDetails }] },
      { name: "Poster Design", items: [{ title: "Poster Design", detailsComponent: PosterDesignDetails }] },
      { name: "Logo Design", items: [{ title: "Logo Design", detailsComponent: LogoDesignDetails }] },
      { name: "UI/UX Design", items: [{ title: "UI/UX Design", detailsComponent: UIUXDesignDetails }] },
    ],
  },
  {
    school: "Zenith",
    categories: [
      { name: "Fastest Line Following Bot", items: [{ title: "Fastest Line Following Bot", detailsComponent: LineBotDetails }] },
      { name: "Innovation Challenge", items: [{ title: "Innovation Challenge", detailsComponent: InnovationChallengeDetails }] },
    ],
  },
  {
    school: "Orthodox",
    categories: [
      { name: "Quiz", items: [{ title: "Inter School Quiz", detailsComponent: QuizChallengeDetails }] },
    ],
  },
];

// Map school names to colors
const schoolColors = {
  All: "#6e6e6e0d",
  Armitage: "#030e50",
  Insignia: "#320a8e",
  Zenith: "#025445",
  Orthodox: "#048490ff",
};

// helper to create URL-friendly slugs
const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-");

// build slug -> school map
const slugToSchool = competitionsData.reduce((acc, c) => {
  acc[slugify(c.school)] = c.school;
  return acc;
}, {});

export default function Competitions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { school: schoolParam } = useParams();

  const [selectedSchool, setSelectedSchool] = useState("All");
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formCompetition, setFormCompetition] = useState(null);

  // added submit form state
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitCompetition, setSubmitCompetition] = useState(null);
  const [showICTSocietyPage, setShowICTSocietyPage] = useState(false);

  const schools = ["All", ...competitionsData.map((c) => c.school)];

  // Official Firebase Storage link for Rules & Regulations (BTUI'26_RnR.pdf)
  const rnrPdfLink = "https://firebasestorage.googleapis.com/v0/b/btui-25.firebasestorage.app/o/BTUI%2726_RnR.pdf?alt=media&token=4d0cdc8c-d6df-4d86-b506-14562e94c17b";

  // When route param changes, update selectedSchool
  useEffect(() => {
    // show ICT Society page when visiting /competitions/ict-society
    if (schoolParam === "most-popular-ict-society") {
      setShowICTSocietyPage(true);
      setSelectedSchool("All");
      return;
    } else {
      setShowICTSocietyPage(false);
    }

    if (schoolParam) {
      const mapped = slugToSchool[schoolParam.toLowerCase()];
      setSelectedSchool(mapped || "All");
    } else {
      setSelectedSchool("All");
    }
  }, [schoolParam]);

  // navigate when user clicks filter buttons
  const handleFilterClick = (school) => {
    setSelectedSchool(school);
    if (school === "All") {
      navigate("/competitions");
    } else {
      navigate(`/competitions/${slugify(school)}`);
    }
  };

  const filteredCompetitions =
    selectedSchool === "All"
      ? competitionsData
      : competitionsData.filter((c) => c.school === selectedSchool);

  if (showICTSocietyPage) {
    return <ICTSocietyPage onBack={() => setShowICTSocietyPage(false)} />;
  }

  return (
    <div className="competitions-container">
      <h1 className="competitions-title">COMPETITIONS</h1>

      {/* Filter Bar */}
      <div className="filter-bar">
        {schools.map((school) => (
          <button
            key={school}
            className={`filter-button glass-btn ${selectedSchool === school ? "active" : ""}`}
            style={{
              backgroundColor: schoolColors[school],
              color: "#fff",
            }}
            onClick={() => handleFilterClick(school)}
          >
            {school === "All" ? "All Competitions" : school}
          </button>
        ))}
        <button
          className="filter-button special-btn"
          style={{ backgroundColor: "#FFA500", color: "#fff" }}
          onClick={() => navigate("/competitions/most-popular-ict-society")}
        >
          Most Popular ICT Society
        </button>
        <a
          className="filter-button glass-btn"
          href={rnrPdfLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{ backgroundColor: "#1E90FF", color: "#fff", textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          Rules & Regulations
        </a>
      </div>

      {/* Cards */}
      {!selectedCompetition && (
        <div className="competition-cards-area">
          <div className="competition-cards">
            {filteredCompetitions.map((comp) =>
              comp.categories.map((cat) =>
                cat.items.map((item) => (
                  <div
                    className="competition-card"
                    key={`${comp.school}-${item.title}`}
                  >
                    <img
                      src={`/${competitionImages[item.title] || "placeholder.png"}`}
                      alt={item.title}
                      className="card-image"
                    />
                    <div className="card-text">
                      <h2>{item.title}</h2>
                      <p>{comp.school}</p>
                    </div>
                    <div className="card-buttons">
                      <button
                        className="card-btn glass-btn"
                        id="details-btn"
                        onClick={() =>
                          setSelectedCompetition({
                            ...item,
                            school: comp.school,
                            category: cat.name,
                          })
                        }
                      >
                        More Details
                      </button>
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </div>
      )}

      {/* Competition Details */}
      {selectedCompetition && (
        <div className="competition-details">
          <button className="back-btn glass-btn" onClick={() => setSelectedCompetition(null)}>
            <FaArrowLeft /> Back
          </button>

          {selectedCompetition.detailsComponent &&
            React.createElement(selectedCompetition.detailsComponent)}

          <button
            className="register-btn glass-btn"
            onClick={() => {
              setFormCompetition(selectedCompetition);
              setShowForm(true);
            }}
          >
            Register Now
          </button>

          {/* Submit Now button - opens submission form (hidden for Inter School Quiz) */}
          {selectedCompetition.title !== "Inter School Quiz" && (
            <button
              className="submit-btn glass-btn"
              onClick={() => {
                setSubmitCompetition(selectedCompetition);
                setShowSubmit(true);
              }}
              style={{ marginLeft: "12px", backgroundColor: "#28a745", color: "#fff" }}
            >
              Submit Now
            </button>
          )}
        </div>
      )}

      {/* Registration Form */}
      {showForm && formCompetition && (
        <CompetitionForm
          competition={formCompetition}
          onClose={() => {
            setShowForm(false);
            setFormCompetition(null);
          }}
        />
      )}

      {/* Submission Form */}
      {showSubmit && submitCompetition && (
        <CompetitionSubmitForm
          competition={submitCompetition}
          onClose={() => {
            setShowSubmit(false);
            setSubmitCompetition(null);
          }}
        />
      )}
    </div>
  );
}
